import type { CreateMedicationsBulk, Days, FrequencyUnit } from "../../../api";

/**
 * The create payload for one medication, assembled from a field set the type
 * system can check.
 *
 * Both ways into the endpoint — the add dialog and the group correction that
 * recreates an existing record — are group-agnostic: each payload element is a
 * discriminated union keyed by `medication_group`, and the group fixes which
 * name/dose/unit fields are valid, but it is only known at runtime. So neither
 * caller can narrow to a single union member, and the assembled object has to
 * be cast. That cast is here rather than at each call site: it would otherwise
 * also swallow a field one caller forgot, which is how the recreate path came
 * to drop the frequency unit.
 */
export function medicationCreatePayload(
  fields: MedicationCreateFields,
): CreateMedicationsBulk["medications"][number] {
  return fields as CreateMedicationsBulk["medications"][number];
}

/**
 * Every field the callers own, none optional: a new one has to be answered
 * everywhere it is sent from, rather than going quietly missing. Server-owned
 * fields (uuid, timestamps, history, study visits) and `user_uuid` are not part
 * of it — the record belongs to whoever creates it.
 */
export type MedicationCreateFields = {
  medication_group: string;
  medication_name: string;
  medication_started: string | null;
  medication_ended: string | null;
  medication_dose: number | null;
  medication_unit: string | null;
  medication_dosage: [number, number, number, number];
  medication_frequency_regular_repeat_every: number | null;
  medication_frequency_regular_repeat_every_unit: FrequencyUnit | undefined;
  medication_frequency_on_days: Array<Days> | null;
  comment: string | null;
  patient_id: number;
  /** Whether the API logs a therapy-change event for this medication. */
  medication_change_auto_event: boolean;
};
